/**
 * Desktop PDP: collapse product description by heading / bold-only lines.
 * Auto-collapses after a delay from data-judy-desc-collapse-delay (0 = off).
 *
 * Description markup lives on <rte-formatter class="text-block rte"> (not a nested .rte).
 */
(function () {
  const DESKTOP_MQ = window.matchMedia('(min-width: 750px)');
  const HEADING_RE = /^H[1-6]$/;

  const isElement = function (node) {
    return node instanceof HTMLElement;
  };

  const visibleText = function (el) {
    return (el.textContent || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
  };

  const findDescriptionRoot = function (details) {
    return (
      details.querySelector('rte-formatter.text-block') ||
      details.querySelector('.text-block.rte') ||
      details.querySelector('rte-formatter')
    );
  };

  /**
   * Product descriptions may arrive wrapped in nested divs (e.g. pasted ChatGPT HTML).
   * Descend until we find a node whose direct children include section anchors.
   */
  const findContentRoot = function (root) {
    if (!(root instanceof HTMLElement)) return null;
    if (Array.from(root.children).some(isSectionAnchor)) return root;

    const wrappers = root.querySelectorAll(':scope div, :scope section, :scope article');
    for (let i = 0; i < wrappers.length; i += 1) {
      const el = wrappers[i];
      if (!(el instanceof HTMLElement)) continue;
      if (Array.from(el.children).some(isSectionAnchor)) return el;
    }

    // Single-child unwrap fallback
    let current = root;
    for (let depth = 0; depth < 10; depth += 1) {
      const kids = Array.from(current.children).filter(isElement);
      if (kids.length === 1) {
        current = kids[0];
        if (Array.from(current.children).some(isSectionAnchor)) return current;
        continue;
      }
      break;
    }

    return root;
  };

  const significantNodes = function (el) {
    return Array.from(el.childNodes).filter(function (node) {
      if (node.nodeType === Node.TEXT_NODE) {
        return (node.textContent || '').trim().length > 0;
      }
      return node.nodeType === Node.ELEMENT_NODE;
    });
  };

  const isBoldTag = function (el) {
    return isElement(el) && el.matches('strong, b');
  };

  const hasInlineBoldStyle = function (el) {
    if (!isElement(el)) return false;
    const styleAttr = (el.getAttribute('style') || '').toLowerCase();
    if (/font-weight\s*:\s*(bold|bolder|[6-9]00)/.test(styleAttr)) return true;
    return false;
  };

  const isBoldWrapper = function (el) {
    return isBoldTag(el) || hasInlineBoldStyle(el);
  };

  /** Whole block is essentially one bold heading line (e.g. Designer's Pick / Details). */
  const isBoldOnlyBlock = function (el) {
    if (!isElement(el)) return false;
    if (!el.matches('p, div, li')) return false;

    const text = visibleText(el);
    if (!text || text.length > 80) return false;

    const nodes = significantNodes(el);
    if (nodes.length === 1 && nodes[0] instanceof HTMLElement && isBoldWrapper(nodes[0])) {
      return visibleText(nodes[0]) === text;
    }

    if (el.childElementCount === 1 && isBoldWrapper(el.children[0]) && visibleText(el.children[0]) === text) {
      return true;
    }

    // Own font-weight bold on a short line (no mixed body copy).
    if (hasInlineBoldStyle(el) && !el.querySelector('br') && text.split(/\s+/).length <= 8) {
      return true;
    }

    return false;
  };

  const isSectionAnchor = function (el) {
    if (!isElement(el)) return false;
    if (HEADING_RE.test(el.tagName)) return visibleText(el).length > 0;
    if (isBoldTag(el) && visibleText(el).length > 0) return true;
    return isBoldOnlyBlock(el);
  };

  const cloneTriangle = function (details) {
    const tpl =
      details.querySelector('template[id^="JudyDescCollapseTriangle"]') ||
      document.querySelector('template[id^="JudyDescCollapseTriangle"]');
    const wrap = document.createElement('span');
    wrap.className = 'judy-desc-collapse__icon';
    wrap.setAttribute('aria-hidden', 'true');
    if (tpl instanceof HTMLTemplateElement) {
      wrap.appendChild(tpl.content.cloneNode(true));
    }
    return wrap;
  };

  const setExpanded = function (section, expanded) {
    section.classList.toggle('is-expanded', expanded);
    section.classList.toggle('is-collapsed', !expanded);
    const trigger = section.querySelector('.judy-desc-collapse__trigger');
    const panel = section.querySelector('.judy-desc-collapse__panel');
    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
    if (panel instanceof HTMLElement) {
      panel.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    }
  };

  const enhanceRoot = function (root, details) {
    if (!(root instanceof HTMLElement) || root.dataset.judyDescCollapseInit === 'true') return null;

    const children = Array.from(root.children);
    const anchorIndexes = [];
    children.forEach(function (child, index) {
      if (isSectionAnchor(child)) anchorIndexes.push(index);
    });

    if (anchorIndexes.length === 0) return null;

    root.dataset.judyDescCollapseInit = 'true';
    root.classList.add('judy-desc-collapse');

    const fragment = document.createDocumentFragment();
    let cursor = 0;

    if (anchorIndexes[0] > 0) {
      const lead = document.createElement('div');
      lead.className = 'judy-desc-collapse__lead';
      for (let i = 0; i < anchorIndexes[0]; i += 1) {
        lead.appendChild(children[i]);
      }
      fragment.appendChild(lead);
      cursor = anchorIndexes[0];
    }

    const sections = [];

    anchorIndexes.forEach(function (anchorIndex, sectionIndex) {
      const nextAnchor = anchorIndexes[sectionIndex + 1];
      const end = typeof nextAnchor === 'number' ? nextAnchor : children.length;
      const anchorEl = children[anchorIndex];

      const section = document.createElement('div');
      section.className = 'judy-desc-collapse__section is-expanded';

      const panelId = `judy-desc-panel-${details.id || 'pdp'}-${sectionIndex}`;

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'judy-desc-collapse__trigger';
      trigger.setAttribute('aria-expanded', 'true');
      trigger.setAttribute('aria-controls', panelId);

      const label = document.createElement('span');
      label.className = 'judy-desc-collapse__label';
      label.appendChild(anchorEl);

      trigger.appendChild(label);
      trigger.appendChild(cloneTriangle(details));

      const panel = document.createElement('div');
      panel.className = 'judy-desc-collapse__panel';
      panel.id = panelId;
      panel.setAttribute('aria-hidden', 'false');

      const panelInner = document.createElement('div');
      panelInner.className = 'judy-desc-collapse__panel-inner';
      for (let i = anchorIndex + 1; i < end; i += 1) {
        panelInner.appendChild(children[i]);
      }
      panel.appendChild(panelInner);

      section.appendChild(trigger);
      section.appendChild(panel);
      fragment.appendChild(section);
      sections.push(section);
      cursor = end;
    });

    if (cursor < children.length) {
      const trail = document.createElement('div');
      trail.className = 'judy-desc-collapse__trail';
      for (let i = cursor; i < children.length; i += 1) {
        trail.appendChild(children[i]);
      }
      fragment.appendChild(trail);
    }

    root.replaceChildren(fragment);
    return sections;
  };

  const initDetails = function (details) {
    if (!(details instanceof HTMLElement)) return;
    if (details.dataset.judyDescCollapseBound === 'true') return;
    if (!DESKTOP_MQ.matches) return;

    const root = findDescriptionRoot(details);
    if (!(root instanceof HTMLElement)) return;

    const contentRoot = findContentRoot(root);
    if (!(contentRoot instanceof HTMLElement)) return;

    const sections = enhanceRoot(contentRoot, details);
    if (!sections || !sections.length) return;

    details.dataset.judyDescCollapseBound = 'true';

    let userInteracted = false;
    let autoTimer = null;

    const cancelAuto = function () {
      if (autoTimer != null) {
        window.clearTimeout(autoTimer);
        autoTimer = null;
      }
    };

    const collapseAll = function () {
      sections.forEach(function (section) {
        setExpanded(section, false);
      });
    };

    sections.forEach(function (section) {
      const trigger = section.querySelector('.judy-desc-collapse__trigger');
      if (!(trigger instanceof HTMLElement)) return;
      trigger.addEventListener('click', function () {
        userInteracted = true;
        cancelAuto();
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        setExpanded(section, !expanded);
      });
    });

    const delayRaw = details.getAttribute('data-judy-desc-collapse-delay');
    const delaySec = delayRaw == null ? 3 : parseInt(delayRaw, 10);
    if (!Number.isFinite(delaySec) || delaySec <= 0) return;

    autoTimer = window.setTimeout(function () {
      autoTimer = null;
      if (userInteracted) return;
      if (!DESKTOP_MQ.matches) return;
      collapseAll();
    }, delaySec * 1000);
  };

  const boot = function () {
    if (!DESKTOP_MQ.matches) return;
    document.querySelectorAll('.product-details--judy').forEach(initDetails);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  if (typeof DESKTOP_MQ.addEventListener === 'function') {
    DESKTOP_MQ.addEventListener('change', function (event) {
      if (event.matches) boot();
    });
  }
})();
